import type { Message } from "../api";
import { dayLabel } from "./format";
import type { Lang } from "./i18n";

/**
 * The thread as it is drawn: day markers between days, and consecutive
 * messages from one sender gathered into a group so the name and face are
 * shown once rather than on every line.
 */

export type ThreadItem =
  | { type: "day"; key: string; label: string }
  | { type: "msg"; key: string; message: Message; first: boolean; last: boolean };

const GROUP_GAP_MS = 5 * 60_000;

/** Operators are told apart by id; notes never merge with replies. */
function senderKey(m: Message): string {
  return `${m.kind}:${m.operatorId ?? ""}`;
}

export function groupThread(messages: Message[], now: number = Date.now(), lang: Lang = "tr"): ThreadItem[] {
  const items: ThreadItem[] = [];
  let lastDay = "";

  messages.forEach((message, index) => {
    const day = dayLabel(message.createdAt, now, lang);
    if (day !== lastDay) {
      items.push({ type: "day", key: `day-${day}-${index}`, label: day });
      lastDay = day;
    }
    const prev = messages[index - 1];
    const next = messages[index + 1];
    const joinsPrev =
      !!prev &&
      message.kind !== "system" &&
      senderKey(prev) === senderKey(message) &&
      dayLabel(prev.createdAt, now, lang) === day &&
      new Date(message.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_GAP_MS;
    const joinsNext =
      !!next &&
      next.kind !== "system" &&
      senderKey(next) === senderKey(message) &&
      dayLabel(next.createdAt, now, lang) === day &&
      new Date(next.createdAt).getTime() - new Date(message.createdAt).getTime() < GROUP_GAP_MS;

    items.push({
      type: "msg",
      key: message.clientMsgId ?? `m${message.id}`,
      message,
      first: !joinsPrev,
      last: !joinsNext,
    });
  });

  return items;
}

/**
 * Folds new or updated messages into a thread without duplicating them.
 *
 * Matching on clientMsgId as well as id lets the server's copy replace the
 * optimistic one. `pending` is set explicitly: the server's copy has no such
 * field, and a plain spread would leave "sending…" on it for ever.
 */
export function mergeMessages(current: Message[], incoming: Message[]): Message[] {
  const next = current.slice();
  for (const message of incoming) {
    const index = next.findIndex(
      (m) => (m.id > 0 && m.id === message.id) || (m.clientMsgId !== null && m.clientMsgId === message.clientMsgId),
    );
    if (index >= 0) next[index] = { ...next[index]!, ...message, pending: message.pending ?? false };
    else next.push(message);
  }
  return next.sort((a, b) => (a.id > 0 && b.id > 0 ? a.id - b.id : a.createdAt.localeCompare(b.createdAt)));
}
